"use client"

import { Link, useNavigate } from "react-router-dom"

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DetailPageHeader,
  MobileScreenHeader,
  EmptyState,
  HelperMessage,
  Skeleton,
  cn,
  valueLinkClass,
} from "@ibada/ui"
import {
  BanIcon,
  CheckIcon,
  FilePlus2Icon,
  ReceiptTextIcon,
  SendIcon,
  Undo2Icon,
  XIcon,
  type LucideIcon,
} from "lucide-react"
import { BranchType } from "@ibada/shared-types/common"

import { useAuthContextStore } from "@/features/auth/services/auth-context-store"
import { useLocale, useTranslations } from "@/i18n"
import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth"
import { formatMoney } from "@/features/finance/lib/finance-utils"
import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import { FinanceTransferStatus } from "@/features/finance/model/finance-types"
import { useFinanceTransferRequestQuery } from "@/features/finance/services/finance-query"
import { buildFinanceInvestigationHref } from "@/features/finance/lib/finance-investigation-url"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"
import { getBranchDisplayLabel } from "@/shared/lib/branch/branch-display"
import { EntityTimeline } from "@/features/finance/components/shared/entity-timeline"
import { TransferRowActions } from "@/features/finance/containers/transfers/transfer-row-actions"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { useMobileBack } from "@/features/dashboard/hooks/use-mobile-screen"
import { MobilePageSkeleton } from "@/shared/components/mobile-page-skeleton"

const EVENT_ICON: Record<string, LucideIcon> = {
  submitted: SendIcon,
  approved: CheckIcon,
  rejected: XIcon,
  cancelled: BanIcon,
  reversed: Undo2Icon,
}

/** Amber for "waiting", green for agreed, red for refused — the same vocabulary the status badge uses. */
const EVENT_TONE: Record<string, string> = {
  submitted: "bg-amber-500/15 text-amber-500",
  approved: "bg-emerald-500/15 text-emerald-500",
  rejected: "bg-destructive/15 text-destructive",
  cancelled: "bg-muted text-muted-foreground",
  reversed: "bg-amber-500/15 text-amber-500",
}

const actorName = (user: { firstName?: string | null; lastName?: string | null } | null | undefined) => {
  const name = user ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() : ""
  return name.length > 0 ? name : null
}

const STATUS_VARIANT: Record<string, string> = {
  [FinanceTransferStatus.Pending]: "warning",
  [FinanceTransferStatus.Approved]: "success",
  [FinanceTransferStatus.Rejected]: "destructive",
}

function formatDateTime(iso: string | null, locale: string) {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(date)
}

/**
 * One cross-context transfer, in full — a ROUTED page, not a dialog.
 *
 * "Who approved this, and when" is a question people send each other links about, and the transfer
 * notifications (submitted / approved / rejected) need somewhere specific to land; they previously dropped
 * the recipient on the list to hunt for the row they had just been told about.
 *
 * **The counterparty's fund and financial account are deliberately withheld.** A transfer is a two-sided
 * agreement, and where the OTHER side filed the money is their bookkeeping, not yours. Both sides are on the
 * wire — the omission is enforced here, at the only place that knows which side is looking.
 */
export function TransferDetailPageContent({ transferId }: { transferId: string }) {
  const t = useTranslations("finance.transferRequests")
  const isMobile = useIsMobile()
  const tMobileNav = useTranslations("churchNav.mobile")
  const goBack = useMobileBack({ fallback: "/finance/transfers" })
  const tCommon = useTranslations("common")
  const locale = useLocale()
  const navigate = useNavigate()
  const branchSlug = useActiveBranchSlug()
  const workspaceSlug = useActiveWorkspaceSlug() ?? undefined
  const workspaceId = useAuthContextStore((state) => state.activeWorkspaceId)
  const { activeBranch, activeContext, canEditFinance } = useFinancePageState()
  const query = useFinanceTransferRequestQuery(transferId || null)
  const listUrl = buildBranchAdminUrl(branchSlug, "/finance/transfers", workspaceSlug)
  const transfer = query.data ?? null

  if (query.isLoading) {
    if (isMobile) return <MobilePageSkeleton hero />
    return (
      <div className='space-y-3'>
        <Skeleton className='h-8 w-64' />
        <Skeleton className='h-40 w-full' />
      </div>
    )
  }

  if (query.isError || !transfer) {
    // A 403/404 must not read as "still loading" — this is the page a notification links to, so the recipient
    // may have lost access to the branch, or the transfer may have been withdrawn.
    return (
      <EmptyState
        tone='danger'
        title={t("detail.error.title")}
        description={t("detail.error.description")}
        action={
          <button type='button' className={cn("text-sm", valueLinkClass)} onClick={() => navigate(listUrl)}>
            {t("detail.back")}
          </button>
        }
      />
    )
  }

  const activeBranchId = activeBranch?.id ?? null
  const isSourceSide = transfer.sourceBranchId === activeBranchId
  const currency = transfer.currency
  const parishLabel = tCommon("scope.parish")
  const ownFund = isSourceSide ? transfer.sourceFund : transfer.destinationFund
  const ownAccount = isSourceSide ? transfer.sourceAccount : transfer.destinationAccount

  const branchLabel = (branch: { name?: string; branchType?: BranchType } | null) =>
    getBranchDisplayLabel({
      branchType: branch?.branchType,
      branchName: branch?.name,
      parishLabel,
      fallbackLabel: "—",
    })

  // The transaction this transfer posted on YOUR side. The counterparty's is deliberately not linked.
  const ownTransactionId = isSourceSide ? transfer.sourceTransactionId : transfer.destinationTransactionId
  // THAT transaction, not a filtered list. `buildFinanceInvestigationHref` is the canonical single-object
  // URL — the investigation page shows the posting, its journal, and the transfer context around it, which is
  // exactly what someone following this link is asking about.
  const transactionHref = ownTransactionId
    ? buildFinanceInvestigationHref(
        { transactionId: ownTransactionId },
        { workspaceSlug, branchSlug, financeContext: activeContext === "parish" ? "parish" : "branch" },
      )
    : null

  const events = [
    { key: "submitted", at: transfer.submittedAt, user: transfer.submittedByUser, note: null as string | null },
    { key: "approved", at: transfer.approvedAt, user: transfer.approvedByUser, note: null },
    { key: "rejected", at: transfer.rejectedAt, user: transfer.rejectedByUser, note: transfer.rejectionReason },
    { key: "cancelled", at: transfer.cancelledAt, user: transfer.cancelledByUser, note: transfer.cancellationReason },
    { key: "reversed", at: transfer.reversedAt, user: null, note: transfer.reversalReason },
  ].filter((event) => Boolean(event.at))

  const timelineItems = [
    {
      id: "created",
      icon: FilePlus2Icon,
      tone: "bg-primary/15 text-primary",
      label: t("detail.events.created"),
      at: transfer.createdAt,
      actorUserId: transfer.createdByUser?.id ?? null,
      actorName: actorName(transfer.createdByUser),
      actorAvatarUrl: transfer.createdByUser?.avatarUrl ?? null,
      note: null as string | null,
    },
    ...events.map((event) => ({
      id: event.key,
      icon: EVENT_ICON[event.key] ?? SendIcon,
      tone: EVENT_TONE[event.key] ?? "bg-muted text-muted-foreground",
      label: t(`detail.events.${event.key}` as never),
      at: event.at as string,
      actorUserId: event.user?.id ?? null,
      actorName: actorName(event.user),
      actorAvatarUrl: event.user?.avatarUrl ?? null,
      note: event.note,
    })),
  ]

  return (
    <div className={isMobile ? "flex flex-col gap-4" : "space-y-4"}>
      {isMobile ? (
        // Phone: the shared screen header (back by the app's back rule, to the transfers list on a cold open from a
        // notification), the route as the subtitle, and every decision in the ⋯ sheet.
        <MobileScreenHeader
          title={t("detail.title")}
          subtitle={`${branchLabel(transfer.sourceBranch)} → ${branchLabel(transfer.destinationBranch)}`}
          back={{ label: tMobileNav("back"), onBack: goBack }}
          actions={
            <TransferRowActions
              transfer={transfer}
              workspaceId={workspaceId}
              branchId={activeBranchId}
              canEdit={canEditFinance}
              onEdit={(item) =>
                navigate(buildBranchAdminUrl(branchSlug, `/finance/transfers/requests/${item.id}/edit`, workspaceSlug))
              }
            />
          }
        />
      ) : (
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <DetailPageHeader
          onBack={() => navigate(listUrl)}
          backLabel={t("detail.back")}
          backHistoryFirst={false}
          title={t("detail.title")}
          description={`${branchLabel(transfer.sourceBranch)} → ${branchLabel(transfer.destinationBranch)}`}
        />
        <TransferRowActions
          transfer={transfer}
          workspaceId={workspaceId}
          branchId={activeBranchId}
          canEdit={canEditFinance}
          onEdit={(item) =>
            navigate(buildBranchAdminUrl(branchSlug, `/finance/transfers/requests/${item.id}/edit`, workspaceSlug))
          }
          presentation='buttons'
        />
      </div>
      )}

      {/* The amount IS the record. Everything else explains where it went and who agreed to it. */}
      <Card>
        <CardContent className='flex flex-wrap items-end justify-between gap-4 py-4'>
          <div className='min-w-0'>
            <Badge className='mb-3' variant={(STATUS_VARIANT[transfer.status] ?? "outline") as never}>
              {t(`status.${transfer.status}` as never)}
            </Badge>
            <p className='text-muted-foreground text-xs font-medium'>{t("table.columns.amount")}</p>
            <p className='font-mono text-3xl font-semibold tabular-nums'>
              {formatMoney(transfer.sourceAmount, currency)}
            </p>
            <p className='text-muted-foreground mt-1 text-xs'>
              {formatDateTime(transfer.occurredAt, locale)}
              {transfer.transferReference || transfer.reference
                ? ` · ${transfer.transferReference ?? transfer.reference}`
                : ""}
            </p>
          </div>

          <dl className='flex flex-wrap gap-6'>
            {/* Only YOUR side's fund and account — see the component docstring. */}
            <Figure label={t("table.columns.fund")} value={ownFund ? ownFund.name : null} />
            <Figure label={t("table.columns.financialAccount")} value={ownAccount?.name} />
            <Figure
              label={t("table.columns.counterparty")}
              value={branchLabel(isSourceSide ? transfer.destinationBranch : transfer.sourceBranch)}
            />
          </dl>
        </CardContent>
      </Card>

      {transfer.description ? (
        <Card size='sm'>
          <CardContent>
            <p className='text-muted-foreground text-xs font-medium'>{t("table.columns.description")}</p>
            <p className='mt-0.5 text-sm whitespace-pre-wrap'>{transfer.description}</p>
          </CardContent>
        </Card>
      ) : null}

      {/* Stated, not left blank: an absent row reads as missing data rather than a deliberate boundary. */}
      <HelperMessage tone='neutral' size='sm'>
        {t("detail.counterpartyPrivacy")}
      </HelperMessage>

      {transactionHref ? (
        <Button asChild variant='outline' size='sm'>
          <Link to={transactionHref}>
            <ReceiptTextIcon className='size-3.5' />
            {t("detail.fields.viewTransaction")}
          </Link>
        </Button>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t("detail.sections.history")}</CardTitle>
        </CardHeader>
        <CardContent>
          <EntityTimeline items={timelineItems} systemLabel={tCommon("system")} />
        </CardContent>
      </Card>
    </div>
  )
}

/** A supporting figure beside the headline amount — deliberately quieter than it. */
function Figure({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null
  return (
    <div>
      <dt className='text-muted-foreground text-xs'>{label}</dt>
      <dd className='text-sm font-medium'>{value}</dd>
    </div>
  )
}
